<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import wootConstants from 'dashboard/constants/globals';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  isOnExpandedLayout: {
    type: Boolean,
    required: true,
  },
  buttonSize: {
    type: String,
    default: 'xs',
  },
});

const emit = defineEmits(['changeFilter']);
const isOnExpandedLayout = computed(() => props.isOnExpandedLayout);
const filterButtonSize = computed(() => props.buttonSize);
const { width } = useWindowSize();
const isSmallScreen = computed(
  () => width.value <= wootConstants.SMALL_SCREEN_BREAKPOINT
);

const store = useStore();
const { t } = useI18n();

const { updateUISettings } = useUISettings();

const chatStatusFilter = useMapGetter('getChatStatusFilter');
const chatSortFilter = useMapGetter('getChatSortFilter');

const showActionsDropdown = ref(false);
const showMobileSortModal = ref(false);
const dropdownRef = ref(null);

const currentStatusFilter = computed(() => {
  return chatStatusFilter.value || wootConstants.STATUS_TYPE.OPEN;
});

const currentSortBy = computed(() => {
  return (
    chatSortFilter.value || wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC
  );
});

const chatStatusOptions = computed(() => [
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.open.TEXT'),
    value: 'open',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.resolved.TEXT'),
    value: 'resolved',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.pending.TEXT'),
    value: 'pending',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.snoozed.TEXT'),
    value: 'snoozed',
  },
  {
    label: t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS.all.TEXT'),
    value: 'all',
  },
]);

const chatSortOptions = computed(() => [
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_asc.TEXT'),
    value: 'last_activity_at_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_desc.TEXT'),
    value: 'last_activity_at_desc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_desc.TEXT'),
    value: 'created_at_desc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_asc.TEXT'),
    value: 'created_at_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_desc.TEXT'),
    value: 'priority_desc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_asc.TEXT'),
    value: 'priority_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.waiting_since_asc.TEXT'),
    value: 'waiting_since_asc',
  },
  {
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.waiting_since_desc.TEXT'),
    value: 'waiting_since_desc',
  },
]);

const activeChatStatusLabel = computed(
  () =>
    chatStatusOptions.value.find(m => m.value === chatStatusFilter.value)
      ?.label || ''
);

const activeChatSortLabel = computed(
  () =>
    chatSortOptions.value.find(m => m.value === chatSortFilter.value)?.label ||
    ''
);

const saveSelectedFilter = (type, value) => {
  updateUISettings({
    conversations_filter_by: {
      status: type === 'status' ? value : currentStatusFilter.value,
      order_by: type === 'sort' ? value : currentSortBy.value,
    },
  });
};

const handleStatusChange = value => {
  emit('changeFilter', value, 'status');
  store.dispatch('setChatStatusFilter', value);
  saveSelectedFilter('status', value);
};

const handleSortChange = value => {
  emit('changeFilter', value, 'sort');
  store.dispatch('setChatSortFilter', value);
  saveSelectedFilter('sort', value);
};

const selectMenuPosition = computed(() => {
  if (isSmallScreen.value) {
    return 'bottom';
  }
  return isOnExpandedLayout.value ? 'left' : 'right';
});

const dropdownStyle = computed(() => {
  if (!dropdownRef.value) {
    return {};
  }
  const rect = dropdownRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const isOverflowingRight = rect.right > viewportWidth;
  const translations = [];
  if (isOverflowingRight) {
    translations.push(`translateX(-${rect.right - viewportWidth + 16}px)`);
  }
  return translations.length ? { transform: translations.join(' ') } : {};
});

const closeDesktopDropdown = () => {
  showActionsDropdown.value = false;
};

const closeMobileSortModal = () => {
  showMobileSortModal.value = false;
};

const toggleSortMenu = () => {
  if (isSmallScreen.value) {
    showMobileSortModal.value = true;
    return;
  }
  showActionsDropdown.value = !showActionsDropdown.value;
};

watch(isSmallScreen, isMobile => {
  if (isMobile) {
    closeDesktopDropdown();
  } else {
    closeMobileSortModal();
  }
});
</script>

<template>
  <div class="relative flex w-full md:w-auto">
    <NextButton
      v-tooltip.right="$t('CHAT_LIST.SORT_TOOLTIP_LABEL')"
      icon="i-lucide-arrow-up-down"
      slate
      faded
      :size="filterButtonSize"
      @click="toggleSortMenu"
    />
    <template v-if="!isSmallScreen">
      <div
        v-if="showActionsDropdown"
        ref="dropdownRef"
        v-on-click-outside="closeDesktopDropdown"
        class="mt-1 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-xl p-4 absolute z-40 top-full w-[min(18rem,85vw)]"
        :style="dropdownStyle"
        :class="[
          isOnExpandedLayout
            ? 'ltr:right-0 rtl:left-0'
            : 'ltr:left-0 rtl:right-0',
        ]"
      >
        <div
          class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between last:mt-4"
        >
          <span class="text-sm truncate text-n-slate-12">
            {{ $t('CHAT_LIST.CHAT_SORT.STATUS') }}
          </span>
          <div class="flex-1">
            <SelectMenu
              :model-value="chatStatusFilter"
              :options="chatStatusOptions"
              :label="activeChatStatusLabel"
              :sub-menu-position="selectMenuPosition"
              :full-width="isSmallScreen"
              @update:model-value="handleStatusChange"
            />
          </div>
        </div>
        <div
          class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between last:mt-4"
        >
          <span class="text-sm truncate text-n-slate-12">
            {{ $t('CHAT_LIST.CHAT_SORT.ORDER_BY') }}
          </span>
          <div class="flex-1">
            <SelectMenu
              :model-value="chatSortFilter"
              :options="chatSortOptions"
              :label="activeChatSortLabel"
              :sub-menu-position="selectMenuPosition"
              :full-width="isSmallScreen"
              @update:model-value="handleSortChange"
            />
          </div>
        </div>
      </div>
    </template>
    <TeleportWithDirection to="body">
      <div
        v-if="isSmallScreen && showMobileSortModal"
        class="fixed inset-0 z-[1000] bg-n-alpha-black1/60 backdrop-blur-[2px]"
        @click.self="closeMobileSortModal"
      >
        <div class="flex flex-col h-full bg-n-solid-1">
          <header
            class="flex items-center justify-between px-4 py-3 border-b border-n-weak"
          >
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('CHAT_LIST.CHAT_SORT_BY_FILTER.TITLE') }}
            </h3>
            <NextButton
              icon="i-lucide-x"
              slate
              ghost
              size="md"
              @click="closeMobileSortModal"
            />
          </header>
          <div class="flex-1 overflow-y-auto p-4">
            <div class="flex flex-col gap-6">
              <div class="flex flex-col gap-3">
                <span class="text-sm truncate text-n-slate-12">
                  {{ $t('CHAT_LIST.CHAT_SORT.STATUS') }}
                </span>
                <SelectMenu
                  :model-value="chatStatusFilter"
                  :options="chatStatusOptions"
                  :label="activeChatStatusLabel"
                  :sub-menu-position="selectMenuPosition"
                  full-width
                  @update:model-value="handleStatusChange"
                />
              </div>
              <div class="flex flex-col gap-3">
                <span class="text-sm truncate text-n-slate-12">
                  {{ $t('CHAT_LIST.CHAT_SORT.ORDER_BY') }}
                </span>
                <SelectMenu
                  :model-value="chatSortFilter"
                  :options="chatSortOptions"
                  :label="activeChatSortLabel"
                  :sub-menu-position="selectMenuPosition"
                  full-width
                  @update:model-value="handleSortChange"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </TeleportWithDirection>
  </div>
</template>
